import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Col, Row } from 'react-bootstrap';
import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import Swal from 'sweetalert2';

import {
  ADD_IMAGES_TO_PRODUCT,
  PRODUCT_IMAGE,
  PRODUCT_IMAGES_DELETE_MULTI,
  PRODUCT_IMAGE_MAKE_THUMBNAIL,
} from 'lib/networking/endpoints';
import { areYouSure } from 'lib/utils/toast';
import { blackberry, salmon } from 'lib/css/colors';
import CloneImagesModal from 'components/global_products/detail_view/assets_view/CloneImagesModal';
import { CUT_DRY_CLIENT_ID } from 'lib/constants';
import ImageUploadModal from 'components/shared/ImageUploadModal';
import { GLOBAL_PRODUCT_TYPE } from 'lib/enums/ProductType';
import ProductAssetsTable from 'components/global_products/detail_view/assets_view/ProductAssetsTable';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';

function GlobalProductImagesView({ refetch, selectedProductOption, selectedProductOptions }) {
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [imageUrls, setImageUrls] = useState([]);
  const [showImageUploadModal, setShowImageUploadModal] = useState(false);
  const [showCloneImagesModal, setShowCloneImagesModal] = useState(false);

  const { data: { other_images: otherImages, thumbnail: thumbnailImage } = {}, loading } = useGet(
    PRODUCT_IMAGE,
    {
      product_id: selectedProductOption?.productId,
      product_type: selectedProductOption?.productType,
      propagate_data: false,
    },
    !selectedProductOption?.productId,
  );

  const { data: { other_images: otherImagesByFlag, thumbnail: thumbnailImageByFlag } = {} } =
    useGet(
      PRODUCT_IMAGE,
      {
        product_id: selectedProductOption?.productId,
        product_type: selectedProductOption?.productType,
        client_id: CUT_DRY_CLIENT_ID,
      },
      !selectedProductOption?.productId,
    );

  const { postData: createThumbnail, loading: thumbCreateLoading } = usePost(
    PRODUCT_IMAGE_MAKE_THUMBNAIL,
    () => {
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Thumbnail created successfully',
        timer: 500,
      });
      refetch();
    },
    error => {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `Error while creating a thumbnail: ${error?.response?.data?.message}`,
        timer: 2000,
      });
    },
  );

  const { postData: deleteMultiImages, loading: deleting } = usePost(
    PRODUCT_IMAGES_DELETE_MULTI,
    () => {
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Successfully deleted',
        timer: 500,
      });
      refetch();
    },
    error => {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `Error while deleting images: ${error?.response?.data?.message}`,
        timer: 2000,
      });
    },
  );

  const { postData: addImages, loading: uploading } = usePost(
    ADD_IMAGES_TO_PRODUCT,
    () => {
      Swal.fire({
        icon: 'success',
        text: 'Images added to the product successfully',
        title: 'Success',
        timer: 500,
      }).then(() => {
        setShowImageUploadModal(false);
        refetch();
      });
    },
    error =>
      Swal.fire({
        icon: 'error',
        text: `An error occurred while assigning images: ${error?.response?.data?.message}`,
        title: 'Error',
      }),
  );

  const addImagesToTheProduct = () => {
    addImages({
      product_id: selectedProductOption.productId,
      product_type: selectedProductOption?.productType,
      uploaded_images: imageUrls,
    });
  };

  if (thumbCreateLoading || loading || uploading) {
    return <LoadingSpinner short />;
  }

  return (
    <>
      <Row className="mt-3">
        {selectedAssets.length ? (
          <Col>
            <Button
              variant="link"
              className={classNames(css(styles.deleteButton), 'float-right mr-3')}
              disabled={!selectedAssets.length || deleting}
              onClick={() =>
                areYouSure(
                  () => deleteMultiImages({ product_image_ids: selectedAssets }),
                  'Are you sure you want to delete these images?',
                )
              }
            >
              Delete
            </Button>
            <Button
              variant="link"
              className={classNames(css(styles.thumbnailButton), 'float-right mr-2')}
              disabled={!selectedAssets.length || selectedAssets.length > 1 || thumbCreateLoading}
              onClick={() => createThumbnail({ product_image_id: selectedAssets[0] })}
            >
              Make Thumbnail
            </Button>
          </Col>
        ) : null}
      </Row>
      <div className="my-4">
        <Row>
          <Col>
            <Button
              disabled={selectedProductOption === GLOBAL_PRODUCT_TYPE}
              className="float-right"
              onClick={() => setShowImageUploadModal(true)}
            >
              Upload Images
            </Button>
          </Col>
        </Row>
        {/* product images by considering the images flag  */}
        {otherImagesByFlag?.length || thumbnailImageByFlag?.url ? (
          <ProductAssetsTable
            otherImages={otherImagesByFlag}
            thumbnailImage={thumbnailImageByFlag}
            selectedAssets={selectedAssets}
            setSelectedAssets={setSelectedAssets}
          />
        ) : null}
        <hr className="my-4" />
        <div className="d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Own Images</h4>
          {selectedProductOptions?.length > 1 &&
            selectedProductOption?.productType !== GLOBAL_PRODUCT_TYPE && (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setShowCloneImagesModal(true)}
              >
                Clone Images to
              </Button>
            )}
        </div>
        {/* product owned images */}
        {otherImages?.length || thumbnailImage?.url ? (
          <ProductAssetsTable
            otherImages={otherImages}
            thumbnailImage={thumbnailImage}
            selectedAssets={selectedAssets}
            setSelectedAssets={setSelectedAssets}
          />
        ) : (
          <div className="m-3">This product has no assets yet</div>
        )}
        {showImageUploadModal ? (
          <ImageUploadModal
            onHide={() => setShowImageUploadModal(false)}
            onSubmit={addImagesToTheProduct}
            header="Upload images"
            imageUrls={imageUrls}
            setImageUrls={setImageUrls}
          />
        ) : null}
        {showCloneImagesModal && (
          <CloneImagesModal
            show={showCloneImagesModal}
            onHide={() => {
              setShowCloneImagesModal(false);
              setSelectedAssets([]);
            }}
            sourceProductOption={selectedProductOption}
            selectedProductOptions={selectedProductOptions || []}
            selectedImageIds={selectedAssets}
            refetch={refetch}
          />
        )}
      </div>
    </>
  );
}

const styles = StyleSheet.create({
  thumbnailButton: {
    color: blackberry,
  },
  deleteButton: {
    color: salmon,
  },
});

GlobalProductImagesView.propTypes = {
  selectedProductOption: PropTypes.object.isRequired,
  selectedProductOptions: PropTypes.array,
  refetch: PropTypes.func,
};

export default GlobalProductImagesView;
